/**
 * ShowcaseTour — the autonomous "showcase reel" launched from
 * Settings → Developer → "Play showcase reel."
 *
 * Requested verbatim: "kind of like a splash screen sequence video,
 * where it does the splash screen, and then moves around the UI on
 * its own, showcasing certain features, and then when you tap the
 * screen it goes out of it."
 *
 * Two hands-off phases:
 *
 *   1. "intro" — the real <SplashScreen>, unmodified. Not a redo of
 *      its recipe; the actual cold-launch component, so this is
 *      pixel-identical to the real thing and can never drift from it.
 *
 *   2. "tour" — cycles the real NAV_DESTINATIONS (the same six
 *      identities the Aperture Bar / rail read from — see
 *      lib/navDestinations.ts) via real router navigation, holding
 *      each for TOUR_STOP_MS before moving on, looping forever. This
 *      drives the ACTUAL app rather than a mockup: whatever Today /
 *      Nova / Clients / Projects / Galleries / Money render for this
 *      account is what plays. A glass caption bar up top names the
 *      stop and reads its NAV_DESTINATION_BLURB, tinted with that
 *      destination's section accent via the `--orbit-chrome-*` set —
 *      the theme-independent tones the Aperture pill itself uses,
 *      since this bar is the same kind of permanent dark chrome.
 *
 * Preview Business switches on for the duration (only if the account
 * has canUseDemo and it wasn't on already) so the tour always has
 * something real-looking to show even on a brand-new account, and
 * switches back to exactly whatever it was before on exit — the reel
 * shouldn't leave a setting flipped the studio didn't ask for.
 *
 * Exit is one gesture everywhere: a transparent full-viewport layer
 * sits above the entire app for both phases, and any tap/click ends
 * the reel immediately — "when you tap the screen it goes out of
 * it," not a button to hunt for. Escape does the same for keyboard
 * use. Nothing underneath is reachable while the reel plays, which
 * doubles as a safety property — an autonomous loop can't be steered
 * into triggering a real action on real data mid-cycle.
 *
 * Mounted once near the router root in App.tsx, beside
 * <NovaLauncher /> — same reasoning: needs router context, and must
 * survive route changes instead of unmounting with them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SplashScreen from '@/components/shared/SplashScreen';
import { useShowcaseMode } from '@/contexts/ShowcaseModeContext';
import { useDemoMode } from '@/contexts/DemoModeContext';
import {
  NAV_DESTINATIONS,
  NAV_DESTINATION_BLURB,
  sectionAccentForNavId,
} from '@/lib/navDestinations';

// How long each destination holds before the reel moves on. Long
// enough to actually look at what loaded, short enough that a full
// six-stop loop reads as a reel rather than a wait.
const TOUR_STOP_MS = 5000;

export default function ShowcaseTour() {
  const { isShowcaseActive, stopShowcase } = useShowcaseMode();
  // Remounts fresh every time isShowcaseActive flips true, so each
  // play starts clean at the splash with phase/stopIdx/refs reset.
  return isShowcaseActive ? <ActiveShowcaseTour onExit={stopShowcase} /> : null;
}

function ActiveShowcaseTour({ onExit }: { onExit: () => void }) {
  const navigate = useNavigate();
  const { canUseDemo, isDemo, setDemo } = useDemoMode();

  const [phase, setPhase] = useState<'intro' | 'tour'>('intro');
  const [stopIdx, setStopIdx] = useState(0);

  // Snapshot of Preview Business's state from the instant the reel
  // started, so exit restores it exactly rather than always forcing
  // it off underneath a studio who already had it on.
  const priorIsDemo = useRef(isDemo);
  const didEnableDemo = useRef(false);

  const exit = useCallback(() => {
    if (didEnableDemo.current) setDemo(priorIsDemo.current);
    onExit();
  }, [onExit, setDemo]);

  // Escape mirrors the tap-anywhere exit for keyboard use, same
  // convention WelcomeSequence / FeatureTour use for their own Skip.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') exit();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exit]);

  // Intro -> tour handoff: turn Preview Business on for the duration
  // if this account can use it and it wasn't already on. Runs once.
  useEffect(() => {
    if (phase !== 'tour') return;
    if (canUseDemo && !priorIsDemo.current) {
      setDemo(true);
      didEnableDemo.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Drive the tour itself: land on each destination in turn, hold,
  // advance, loop forever until the click-catcher below ends it.
  useEffect(() => {
    if (phase !== 'tour') return;
    navigate(NAV_DESTINATIONS[stopIdx].href);
    const t = window.setTimeout(() => {
      setStopIdx((i) => (i + 1) % NAV_DESTINATIONS.length);
    }, TOUR_STOP_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, stopIdx]);

  const dest = NAV_DESTINATIONS[stopIdx];
  const accent = sectionAccentForNavId(dest.id);
  const accentColor = `hsl(var(--orbit-chrome-${accent}))`;

  return (
    <div
      // The click-catcher. Outermost and covers the full viewport in
      // both phases so ANY tap, anywhere, exits — not just a corner
      // button. Transparent: the splash (phase 1) or the real app
      // (phase 2) render underneath, unobstructed except the caption
      // bar itself.
      className="fixed inset-0 z-[100000] cursor-pointer"
      onClick={exit}
      role="button"
      aria-label="Exit showcase tour"
    >
      {phase === 'intro' ? (
        <SplashScreen onComplete={() => setPhase('tour')} />
      ) : (
        <div className="absolute inset-x-0 top-0 animate-in fade-in duration-300 border-b border-white/10 bg-black/75 px-5 pb-4 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur-xl sm:px-8">
          <div className="mx-auto flex max-w-3xl items-center gap-4">
            <div className="min-w-0 flex-1">
              <div key={dest.id} className="animate-in fade-in slide-in-from-left-2 duration-300">
                <p
                  className="text-[11px] font-semibold uppercase tracking-[0.16em]"
                  style={{ color: accentColor }}
                >
                  Showcase &middot; {dest.label}
                </p>
                <p className="mt-1 truncate text-sm text-white/80">
                  {NAV_DESTINATION_BLURB[dest.id] ?? ''}
                </p>
              </div>
            </div>
            <p className="hidden shrink-0 text-[11px] font-medium text-white/40 sm:block">
              Tap anywhere to exit
            </p>
          </div>
          <div className="mx-auto mt-3 flex max-w-3xl gap-1.5">
            {NAV_DESTINATIONS.map((d, i) => (
              <span
                key={d.id}
                className="h-[3px] flex-1 rounded-full transition-colors duration-300"
                style={{ backgroundColor: i === stopIdx ? accentColor : 'rgba(255,255,255,0.16)' }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
